import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Dialog} from "primereact/dialog";
import {ConfirmDialog} from "primereact/confirmdialog";
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {classNames} from "primereact/utils";
import {RadioButton} from "primereact/radiobutton";
import {Checkbox} from "primereact/checkbox";
import {useAppDispatch, useAppSelector} from "../../../redux/hooks";
import {setIsSpeacking} from "../../../redux/actions/actionsMonitoringList";
import {fetchAudioControlById} from "../../../redux/api/apiAudioControl";
import {
    completeControl,
    fetchControlResult,
    fetchFormsByCallDetailId,
    fetchTranscriptionByCallId,
    saveManualControl,
} from "../../../redux/api/apiQualityControlStatistics";
import {SET_USER_STATUS_ERROR, SET_USER_STATUS_SUCCESS} from "../../../redux/types/typesUsersList";
import AudioPlayer from "../../Monitoring/AudioPlayer";
import "react-h5-audio-player/lib/styles.css";
import {
    isAudioControlBlockActive,
    isAudioControlBlockCounted,
    isAudioControlConditionActive,
    isAudioControlConditionCounted,
    type AudioControlBlock,
    type AudioControlCondition,
    AUDIO_CONTROL_FAIL_SCOPE_OPTIONS,
    type AudioControlFailListItem,
    type AudioControlFailScope,
} from "../../Settings/AudioControlList/audioControlQuestionnaire";
import {
    MOCK_AI_EVALUATION,
} from "./manualEvaluationMock";
import {
    countTranscriptionSearchMatches,
    countTranscriptionTextSearchMatches,
    escapeRegExp,
    findActiveTranscriptionSegmentIndex,
    findTranscriptionSegmentIndexById,
    findTranscriptionSegmentIndexByStart,
    formatTranscriptionSegments,
    formatTranscriptionTimestamp,
    getTranscriptionMatchingSegmentIndices,
    mapTranscriptionSpeakerLabel,
    parseTranscriptionTextToSegments,
    resolveEvaluationCitationDisplay,
    resolveTranscriptionByCallIdResult,
    segmentMatchesTranscriptionQuery,
    type EvaluationCitationDisplay,
    type TranscriptionSegment,
    TRANSCRIPTION_HUB_STATUS,
} from "./manualEvaluationTranscription";
import {
    normalizeAudioControlDetails,
    normalizeSimpleUsersControls,
    getQuestionnaireStatusMeta,
    QUALITY_CONTROL_FORM_STATUS,
    toEvaluatedQuestionnaireOption,
    type QualityControlSimpleOption,
} from "./manualEvaluationQuestionnaires";
import {
    buildManualEvaluationState,
    buildQuestionnaireFromControlResult,
    calculateBlockInnerScoreFromElements,
    createManualControlElement,
    hasEvaluationCitations,
    isControlResultNotFound,
    isNoViolationStatus,
    MANUAL_CONTROL_ELEMENT_STATUS,
    manualControlItemsToElementsMap,
    normalizeControlResultData,
    parseCitationsFromTag,
    pickElementId,
    getAttachedCitationIdsForElement,
    serializeCitationsToTag,
    serializeManualControlBlocks,
    type ControlResultEnvelope,
    type ManualControlApiItem,
    type ManualControlElementsMap,
    type ManualEvaluationBlockScoreMap,
    type ManualEvaluationCitation,
    type ManualEvaluationState,
} from "./manualEvaluationControl";
import "./ManualEvaluationDialog.scss";

export type {
    ManualControlApiItem,
    ManualControlElementsMap,
    ManualEvaluationCitation,
    ManualEvaluationBlockScoreMap,
} from "./manualEvaluationControl";

export type ManualEvaluationSelectedFailListMap = Record<string, boolean>;

type ManualEvaluationDialogProps = {
    visible: boolean;
    onHide: () => void;
    variant?: 'evaluation' | 'controlResultView';
    audioControlId?: number | null;
    callId?: number | null;
    sessionId?: string | null;
    callStatus?: number | null;
    transcription?: any;
    initialEvaluation?: any;
    manualInitialEvaluation?: any;
    aiInitialEvaluation?: any;
    readOnly?: boolean;
    evaluationMode?: 'manual' | 'ai';
    validAudioUrl?: any[];
    audioUrl?: any[] | null;
    audioUrlLoading?: boolean;
    hasPlayerVisiblePermission?: boolean;
    onLoadAudio?: () => void;
    handleResetPlayer?: () => void;
    onEvaluate?: (payload: {
        callId: number | null;
        audioControlId: number | null;
        score: number;
        elements: ManualControlElementsMap;
        blockScores: ManualEvaluationBlockScoreMap;
        selectedFailList: AudioControlFailListItem[];
    }) => void;
};

type ManualEvaluationViolationOption = 'element' | 'none';

const VIOLATION_OPTIONS: { value: ManualEvaluationViolationOption; label: string }[] = [
    {value: 'element', label: 'Порушення'},
    {value: 'none', label: 'Немає порушень'},
];

const EVALUATION_MODE_OPTIONS: { value: 'manual' | 'ai'; label: string }[] = [
    {value: 'manual', label: 'Ручна оцінка'},
    {value: 'ai', label: 'ШІ оцінка'},
];

const getInitialEvaluationForMode = (
    mode: 'manual' | 'ai',
    manualInitialEvaluation?: any,
    aiInitialEvaluation?: any,
    fallbackInitialEvaluation?: any,
) => {
    if (mode === 'ai') {
        return aiInitialEvaluation ?? fallbackInitialEvaluation ?? MOCK_AI_EVALUATION;
    }

    return manualInitialEvaluation ?? fallbackInitialEvaluation;
};

const resolveInitialManualEvaluationState = (
    value: unknown,
    blocks: AudioControlBlock[],
): ManualEvaluationState => {
    if (Array.isArray(value)) {
        return buildManualEvaluationState(value, blocks);
    }

    if (!value || typeof value !== 'object') {
        return {
            elements: {},
            blockScores: {},
        };
    }

    const typedValue = value as Partial<ManualEvaluationState> & { elements?: unknown };
    if (Array.isArray(typedValue.elements)) {
        return buildManualEvaluationState(typedValue.elements, blocks);
    }

    if (typedValue.elements && typeof typedValue.elements === 'object') {
        const elements = typedValue.elements as ManualControlElementsMap;

        return {
            elements,
            blockScores: typedValue.blockScores ?? {},
        };
    }

    return {
        elements: {},
        blockScores: typedValue.blockScores ?? {},
    };
};

const SHOW_QUESTIONNAIRE_EVALUATION_SCHEME = false;

const formatControlResultDateTime = (value?: string | null) => {
    if (!value) {
        return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '-';
    }

    return date.toLocaleString('uk-UA');
};

const formatQuestionnaireMetaValue = (value: string | null | undefined) => {
    const normalizedValue = String(value ?? '').trim();
    return normalizedValue || '- - -';
};

const renderQuestionnaireMetaGrid = (
    fields: Array<{ label: string; value: string | null | undefined }>,
    className?: string,
) => (
    <div className={classNames('manual-evaluation-meta-grid', className)}>
        {fields.map((field) => (
            <div className="manual-evaluation-meta-field" key={field.label}>
                <div className="manual-evaluation-meta-field-label">{field.label}</div>
                <div className="manual-evaluation-meta-field-value">{formatQuestionnaireMetaValue(field.value)}</div>
            </div>
        ))}
    </div>
);

const buildInitialExpandedBlocks = (blocks: AudioControlBlock[]) => {
    const initialExpandedBlocks: Record<number, boolean> = {};

    blocks.forEach((block, blockIndex) => {
        if (isAudioControlBlockCounted(block) && isAudioControlBlockActive(block)) {
            initialExpandedBlocks[blockIndex] = false;
        }
    });

    return initialExpandedBlocks;
};

const getCitationKey = (elementId: number, citationIndex: number) => `${elementId}-citation-${citationIndex}`;

const VIOLATION_DESCRIPTION_MAX_LENGTH = 2000;
const CITATION_COMMENT_MAX_LENGTH = 200;

const limitTextLength = (value: string, maxLength: number) => value.slice(0, maxLength);

const renderCitationTimestampButton = (
    citation: Pick<EvaluationCitationDisplay, 'start'>,
    onTimestampClick?: (startSec: number) => void,
) => {
    if (citation.start == null || !Number.isFinite(citation.start)) {
        return null;
    }

    return (
        <button
            type="button"
            className="manual-evaluation-transcription-timestamp manual-evaluation-transcription-timestamp--interactive"
            title="Показати фрагмент у розшифровці"
            onClick={(event) => {
                event.stopPropagation();
                onTimestampClick?.(citation.start as number);
            }}
        >
            {formatTranscriptionTimestamp(citation.start)}
        </button>
    );
};

const renderCitationMetaContent = (
    citation: Pick<EvaluationCitationDisplay, 'start' | 'speaker'>,
    onTimestampClick?: (startSec: number) => void,
) => {
    const timestampButton = renderCitationTimestampButton(citation, onTimestampClick);

    if (!timestampButton && !citation.speaker) {
        return null;
    }

    return (
        <div className="manual-evaluation-citation-meta">
            {timestampButton}
            {citation.speaker && (
                <span className="manual-evaluation-citation-speaker">{citation.speaker}</span>
            )}
        </div>
    );
};

const getFailListItemKey = (item: AudioControlFailListItem, index: number) =>
    item.id != null && item.id > 0 ? `id-${item.id}` : `order-${item.orderId}-${index}`;

const getElementStatusOption = (status: number | undefined): ManualEvaluationViolationOption | undefined => {
    if (status == null) {
        return undefined;
    }

    return isNoViolationStatus(status) ? 'none' : 'element';
};

const getStatusFromOption = (option: ManualEvaluationViolationOption) =>
    option === 'none'
        ? MANUAL_CONTROL_ELEMENT_STATUS.NO_VIOLATION
        : MANUAL_CONTROL_ELEMENT_STATUS.VIOLATION;

const isViolationMarked = (option: ManualEvaluationViolationOption | undefined): boolean =>
    Boolean(option && option !== 'none');

const getEffectiveFailScope = (
    condition: AudioControlCondition,
    option: ManualEvaluationViolationOption | undefined,
): AudioControlFailScope | null => {
    if (!isViolationMarked(option)) {
        return null;
    }

    return condition.failScope ?? 'element';
};

const hasBlockQuestionnaireLevelFail = (
    block: AudioControlBlock,
    elements: ManualControlElementsMap,
): boolean =>
    (block.conditions ?? []).some((condition) => {
        if (!isAudioControlConditionCounted(condition) || !isAudioControlConditionActive(condition)) {
            return false;
        }

        const elementId = pickElementId(condition.id);
        if (elementId == null) {
            return false;
        }

        const option = getElementStatusOption(elements[elementId]?.status);
        return getEffectiveFailScope(condition, option) === 'questionnaire';
    });

const hasQuestionnaireLevelFail = (
    blocks: AudioControlBlock[],
    elements: ManualControlElementsMap,
): boolean =>
    blocks.some((block) => hasBlockQuestionnaireLevelFail(block, elements));

const hasBlockLevelFail = (
    block: AudioControlBlock,
    elements: ManualControlElementsMap,
): boolean =>
    (block.conditions ?? []).some((condition) => {
        if (!isAudioControlConditionCounted(condition) || !isAudioControlConditionActive(condition)) {
            return false;
        }

        const elementId = pickElementId(condition.id);
        if (elementId == null) {
            return false;
        }

        const option = getElementStatusOption(elements[elementId]?.status);
        return getEffectiveFailScope(condition, option) === 'block';
    });

const formatWeight = (value: number | null | undefined) => {
    if (value == null) {
        return '- - -';
    }
    return `${value % 1 === 0 ? Math.round(value) : value.toFixed(1)}%`;
};

const formatTranscriptionContent = (content: unknown) => {
    if (content == null || content === '') {
        return '- - -';
    }

    let text: string;
    if (typeof content === 'string') {
        text = content;
    } else {
        text = JSON.stringify(content, null, 2);
    }

    return text
        .replace(/Участник 1:/g, 'Оператор:')
        .replace(/Участник 2:/g, 'Клієнт:');
};

const TRANSCRIPTION_TIMESTAMP_PATTERN = /\((\d{1,2}):(\d{2})\)/g;

const parseTranscriptionTimestampToMs = (minutes: number, seconds: number) => {
    return (minutes * 60 + seconds) * 1000;
};

const highlightTranscriptionText = (text: string, query: string): React.ReactNode => {
    const normalizedQuery = query.trim();
    if (!normalizedQuery) {
        return text;
    }

    const regex = new RegExp(`(${escapeRegExp(normalizedQuery)})`, 'gi');
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.slice(lastIndex, match.index));
        }

        parts.push(
            <mark
                key={`transcription-highlight-${match.index}`}
                className="manual-evaluation-transcription-highlight"
            >
                {match[0]}
            </mark>
        );

        lastIndex = regex.lastIndex;
    }

    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }

    return parts.length > 0 ? parts : text;
};

const renderTranscriptionWithTimestamps = (
    content: string,
    canSeek: boolean,
    onSeek: (timestampMs: number) => void,
    searchQuery: string,
) => {
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    const regex = new RegExp(TRANSCRIPTION_TIMESTAMP_PATTERN.source, 'g');
    let match: RegExpExecArray | null;

    while ((match = regex.exec(content)) !== null) {
        if (match.index > lastIndex) {
            parts.push(highlightTranscriptionText(content.slice(lastIndex, match.index), searchQuery));
        }

        const timestampMs = parseTranscriptionTimestampToMs(Number(match[1]), Number(match[2]));

        parts.push(
            <button
                key={`transcription-timestamp-${match.index}`}
                type="button"
                className={classNames('manual-evaluation-transcription-timestamp', {
                    'manual-evaluation-transcription-timestamp--interactive': canSeek,
                })}
                disabled={!canSeek}
                title={canSeek ? 'Перейти до моменту в записі' : undefined}
                onClick={() => onSeek(timestampMs)}
            >
                {match[0]}
            </button>
        );

        lastIndex = regex.lastIndex;
    }

    if (lastIndex < content.length) {
        parts.push(highlightTranscriptionText(content.slice(lastIndex), searchQuery));
    }

    return parts;
};

const resolveTranscriptionSpeakerLabel = (speakerId?: string | number | null) => {
    if (speakerId == null || speakerId === '') {
        return 'Учасник';
    }

    if (speakerId === 'Оператор' || speakerId === 'Клієнт' || speakerId === 'Учасник') {
        return speakerId;
    }

    return mapTranscriptionSpeakerLabel(speakerId);
};

const renderTranscriptionSegments = (
    segments: TranscriptionSegment[],
    activeSegmentIndex: number,
    canSeekAudio: boolean,
    onSeek: (timestampMs: number) => void,
    registerSegmentRef: (index: number, element: HTMLDivElement | null) => void,
    searchQuery: string,
    currentSearchSegmentIndex: number | null,
    citationHighlightSegmentIndex: number | null = null,
    citationPickMode = false,
    pendingCitationSegmentIndexes: number[] = [],
    attachedCitationSegmentIds: Set<number> = new Set(),
    onCitationSegmentToggle?: (index: number, checked: boolean) => void,
) => (
    segments.map((segment, index) => {
        const timestampMs = (segment.start ?? 0) * 1000;
        const isActive = index === activeSegmentIndex;
        const isCitationHighlight = citationHighlightSegmentIndex === index;
        const hasSearchQuery = Boolean(searchQuery.trim());
        const isSearchMatch = segmentMatchesTranscriptionQuery(segment, searchQuery);
        const isCurrentSearchMatch = currentSearchSegmentIndex === index;
        const isPendingCitation = pendingCitationSegmentIndexes.includes(index);
        const segmentCitationId = segment.id != null && segment.id !== '' ? Number(segment.id) : null;
        const isAlreadyAttachedCitation = segmentCitationId != null
            && Number.isFinite(segmentCitationId)
            && attachedCitationSegmentIds.has(segmentCitationId);

        return (
            <div
                key={`transcription-segment-${segment.id ?? index}`}
                ref={(element) => registerSegmentRef(index, element)}
                data-segment-index={index}
                className={classNames('manual-evaluation-transcription-segment', {
                    'manual-evaluation-transcription-segment--active': isActive,
                    'manual-evaluation-transcription-segment--citation-highlight': isCitationHighlight,
                    'manual-evaluation-transcription-segment--search-match': hasSearchQuery && isSearchMatch,
                    'manual-evaluation-transcription-segment--search-current': hasSearchQuery && isCurrentSearchMatch,
                    'manual-evaluation-transcription-segment--search-dimmed': hasSearchQuery && !isSearchMatch,
                    'manual-evaluation-transcription-segment--pickable': citationPickMode && !isAlreadyAttachedCitation,
                    'manual-evaluation-transcription-segment--pick-pending': isPendingCitation,
                    'manual-evaluation-transcription-segment--pick-attached': citationPickMode && isAlreadyAttachedCitation,
                })}
            >
                {citationPickMode && (
                    <Checkbox
                        inputId={`transcription-citation-${index}`}
                        className="manual-evaluation-transcription-citation-checkbox"
                        checked={isPendingCitation}
                        disabled={isAlreadyAttachedCitation}
                        onChange={(changeEvent) => onCitationSegmentToggle?.(index, changeEvent.checked ?? false)}
                        onClick={(event) => event.stopPropagation()}
                    />
                )}
                <div className="manual-evaluation-transcription-segment-content">
                    <button
                        type="button"
                        className="manual-evaluation-transcription-timestamp manual-evaluation-transcription-timestamp--interactive"
                        title={canSeekAudio ? 'Перейти до моменту в записі' : 'Показати момент у розшифровці'}
                        onClick={(event) => {
                            event.stopPropagation();
                            onSeek(timestampMs);
                        }}
                    >
                        {formatTranscriptionTimestamp(segment.start ?? 0)}
                    </button>
                    {' '}
                    <span className="manual-evaluation-transcription-speaker">
                        {highlightTranscriptionText(
                            `${resolveTranscriptionSpeakerLabel(segment.speakerId)}:`,
                            searchQuery,
                        )}
                    </span>
                    {' '}
                    <span className="manual-evaluation-transcription-segment-text">
                        {highlightTranscriptionText(String(segment.text ?? ''), searchQuery)}
                    </span>
                </div>
            </div>
        );
    })
);

const ManualEvaluationDialog = ({
    visible,
    onHide,
    variant = 'evaluation',
    audioControlId,
    callId = null,
    sessionId = null,
    callStatus = null,
    transcription,
    initialEvaluation,
    manualInitialEvaluation,
    aiInitialEvaluation,
    readOnly = false,
    evaluationMode: initialEvaluationMode = 'manual',
    validAudioUrl = [],
    audioUrl = null,
    audioUrlLoading = false,
    hasPlayerVisiblePermission = false,
    onLoadAudio,
    handleResetPlayer,
    onEvaluate,
}: ManualEvaluationDialogProps) => {
    const dispatch = useAppDispatch();
    const jwtToken = useAppSelector(state => state.UsersReducer.userData.jwtToken?.jwtToken);
    const audioUnavailable = useAppSelector(state => state.MonitoringListReducer.audioUnavailable);
    const transcriptionHubStatus = useAppSelector(state => state.WebRTCReducer.transcriptionHubStatus);
    const [submitting, setSubmitting] = useState(false);
    const [showCompleteEvaluationConfirm, setShowCompleteEvaluationConfirm] = useState(false);
    const [blockSavingIndex, setBlockSavingIndex] = useState<number | null>(null);
    const [controlResultLoading, setControlResultLoading] = useState(false);
    const [controlResultNotFound, setControlResultNotFound] = useState(false);
    const [controlResultMeta, setControlResultMeta] = useState<ControlResultEnvelope | null>(null);
    const isControlResultView = variant === 'controlResultView';
    const [timeStamp, setTimeStamp] = useState<number | null>(null);
    const [seekNonce, setSeekNonce] = useState(0);
    const [audioPlayerSessionKey, setAudioPlayerSessionKey] = useState(0);
    const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<QualityControlSimpleOption | null>(null);
    const [questionnaireOptions, setQuestionnaireOptions] = useState<QualityControlSimpleOption[]>([]);
    const [questionnaireOptionsLoading, setQuestionnaireOptionsLoading] = useState(false);
    const [questionnaireBlocks, setQuestionnaireBlocks] = useState<AudioControlBlock[]>([]);
    const [questionnaireFailList, setQuestionnaireFailList] = useState<AudioControlFailListItem[]>([]);
    const [selectedFailList, setSelectedFailList] = useState<ManualEvaluationSelectedFailListMap>({});
    const [questionnaireDetailsLoading, setQuestionnaireDetailsLoading] = useState(false);
    const [questionnaireDetailsError, setQuestionnaireDetailsError] = useState<string | null>(null);
    const [selectedEvaluationMode, setSelectedEvaluationMode] = useState<'manual' | 'ai'>(initialEvaluationMode);
    const [elements, setElements] = useState<ManualControlElementsMap>({});
    const [citationPickTargetElementId, setCitationPickTargetElementId] = useState<number | null>(null);
    const [citationHighlightSegmentIndex, setCitationHighlightSegmentIndex] = useState<number | null>(null);
    const [pendingCitationSegmentIndexes, setPendingCitationSegmentIndexes] = useState<number[]>([]);
    const [expandedCitations, setExpandedCitations] = useState<Record<string, boolean>>({});
    const [blockScores, setBlockScores] = useState<ManualEvaluationBlockScoreMap>({});
    const [expandedBlocks, setExpandedBlocks] = useState<Record<number, boolean>>({});
    const [isFailPanelExpanded, setIsFailPanelExpanded] = useState(true);
    const [showTranscription, setShowTranscription] = useState(false);
    const [transcriptionSearchQuery, setTranscriptionSearchQuery] = useState('');
    const [activeSearchMatchIndex, setActiveSearchMatchIndex] = useState(0);
    const [loadedTranscription, setLoadedTranscription] = useState<string | null>(null);
    const [loadedTranscriptionSegments, setLoadedTranscriptionSegments] = useState<TranscriptionSegment[]>([]);
    const [playbackTimeMs, setPlaybackTimeMs] = useState(0);
    const [transcriptionLoading, setTranscriptionLoading] = useState(false);
    const [transcriptionError, setTranscriptionError] = useState<string | null>(null);
    const [transcriptionPending, setTranscriptionPending] = useState(false);
    const prevHasQuestionnaireFail = useRef(false);
    const dialogActiveRef = useRef(false);
    const dialogWasVisibleRef = useRef(false);
    const audioLoadAttemptedRef = useRef(false);
    const transcriptionAutoLoadKeyRef = useRef<number | null>(null);
    const controlResultReadyForQuestionnaireIdRef = useRef<number | null>(null);
    const transcriptionLoadGenerationRef = useRef(0);
    const transcriptionSegmentRefs = useRef<Array<HTMLDivElement | null>>([]);
    const transcriptionBodyRef = useRef<HTMLDivElement | null>(null);
    const lastScrolledTranscriptionSegmentIndex = useRef(-1);
    const blocks = questionnaireBlocks;
    const isManualMode = selectedEvaluationMode === 'manual';
    const isViewingEvaluatedQuestionnaire = isControlResultView
        || selectedQuestionnaire?.status === QUALITY_CONTROL_FORM_STATUS.COMPLETED;
    const isReadOnly = readOnly || selectedEvaluationMode === 'ai' || isViewingEvaluatedQuestionnaire;
    const canSwitchEvaluationMode = !readOnly && !isViewingEvaluatedQuestionnaire;
    const hasSelectedFailListItem = useMemo(
        () => Object.values(selectedFailList).some(Boolean),
        [selectedFailList],
    );

    useEffect(() => {
        dialogActiveRef.current = visible;
        const justOpened = visible && !dialogWasVisibleRef.current;
        dialogWasVisibleRef.current = visible;

        if (!visible) {
            transcriptionLoadGenerationRef.current += 1;
            dispatch({type: SET_USER_STATUS_SUCCESS, payload: null});
            dispatch({type: SET_USER_STATUS_ERROR, payload: null});
            prevHasQuestionnaireFail.current = false;
            setShowTranscription(false);
            setTranscriptionSearchQuery('');
            setActiveSearchMatchIndex(0);
            setLoadedTranscription(null);
            setLoadedTranscriptionSegments([]);
            setPlaybackTimeMs(0);
            setTranscriptionLoading(false);
            setTranscriptionError(null);
            setTranscriptionPending(false);
            setQuestionnaireOptions([]);
            setSelectedQuestionnaire(null);
            setQuestionnaireOptionsLoading(false);
            setQuestionnaireBlocks([]);
            setQuestionnaireFailList([]);
            setSelectedFailList({});
            setQuestionnaireDetailsLoading(false);
            setQuestionnaireDetailsError(null);
            setIsFailPanelExpanded(!isControlResultView);
            setExpandedBlocks({});
            setCitationPickTargetElementId(null);
            setCitationHighlightSegmentIndex(null);
            setPendingCitationSegmentIndexes([]);
            setElements({});
            setExpandedCitations({});
            setControlResultLoading(false);
            setControlResultNotFound(false);
            setControlResultMeta(null);
            setShowCompleteEvaluationConfirm(false);
            audioLoadAttemptedRef.current = false;
            transcriptionAutoLoadKeyRef.current = null;
            controlResultReadyForQuestionnaireIdRef.current = null;
            setTimeStamp(null);
            setSeekNonce(0);
            setSelectedEvaluationMode(initialEvaluationMode);
            lastScrolledTranscriptionSegmentIndex.current = -1;
            transcriptionSegmentRefs.current = [];
            return;
        }

        if (!justOpened) {
            return;
        }

        setShowTranscription(false);
        setTranscriptionSearchQuery('');
        setActiveSearchMatchIndex(0);
        setLoadedTranscription(null);
        setLoadedTranscriptionSegments([]);
        setPlaybackTimeMs(0);
        setTranscriptionLoading(false);
        setTranscriptionError(null);
        setTranscriptionPending(false);
        setIsFailPanelExpanded(!isControlResultView);
        setSelectedQuestionnaire(null);
        setQuestionnaireBlocks([]);
        setQuestionnaireFailList([]);
        setSelectedFailList({});
        setQuestionnaireDetailsLoading(false);
        setQuestionnaireDetailsError(null);
        setExpandedBlocks({});
        setControlResultLoading(false);
        setControlResultNotFound(false);
        setControlResultMeta(null);
        setTimeStamp(null);
        setSeekNonce(0);
        lastScrolledTranscriptionSegmentIndex.current = -1;
        transcriptionSegmentRefs.current = [];
        setAudioPlayerSessionKey((previousKey) => previousKey + 1);
        setSelectedEvaluationMode(initialEvaluationMode);
        const initialEvaluationState = getInitialEvaluationForMode(
            initialEvaluationMode,
            manualInitialEvaluation,
            aiInitialEvaluation,
            initialEvaluation,
        );

        if (initialEvaluationMode === 'ai') {
            const aiState = resolveInitialManualEvaluationState(initialEvaluationState, []);
            setElements(aiState.elements);
            setBlockScores(aiState.blockScores);
        } else {
            setElements({});
            setBlockScores({});
        }

        setCitationPickTargetElementId(null);
        setPendingCitationSegmentIndexes([]);
    }, [visible, initialEvaluation, manualInitialEvaluation, aiInitialEvaluation, initialEvaluationMode, readOnly, isControlResultView, dispatch]);

    const handleEvaluationModeChange = (mode: 'manual' | 'ai') => {
        setSelectedEvaluationMode(mode);

        const nextInitialEvaluation = getInitialEvaluationForMode(
            mode,
            manualInitialEvaluation,
            aiInitialEvaluation,
            initialEvaluation,
        );

        if (mode === 'ai') {
            const aiState = resolveInitialManualEvaluationState(nextInitialEvaluation, blocks);
            setElements(aiState.elements);
            setBlockScores(aiState.blockScores);
        } else {
            setElements({});
            setBlockScores({});
        }

        setCitationPickTargetElementId(null);
        setPendingCitationSegmentIndexes([]);
        prevHasQuestionnaireFail.current = false;
        setShowTranscription(false);
        setLoadedTranscription(null);
        setLoadedTranscriptionSegments([]);
        setPlaybackTimeMs(0);
        setTranscriptionLoading(false);
        setTranscriptionError(null);
        setTranscriptionPending(false);
        lastScrolledTranscriptionSegmentIndex.current = -1;
    };

    useEffect(() => {
        if (!visible || !jwtToken || !callId) {
            return;
        }

        let isCancelled = false;
        setQuestionnaireOptionsLoading(true);

        fetchFormsByCallDetailId(callId, isControlResultView, jwtToken)
            .then((response) => {
                if (isCancelled) {
                    return;
                }

                if (response.data?.statusCode === 200) {
                    setQuestionnaireOptions(normalizeSimpleUsersControls(response.data.data, {
                        isRated: isControlResultView,
                    }));
                } else {
                    setQuestionnaireOptions([]);
                }
            })
            .catch(() => {
                if (!isCancelled) {
                    setQuestionnaireOptions([]);
                }
            })
            .finally(() => {
                if (!isCancelled) {
                    setQuestionnaireOptionsLoading(false);
                }
            });

        return () => {
            isCancelled = true;
        };
    }, [visible, jwtToken, callId, isControlResultView]);

    useEffect(() => {
        controlResultReadyForQuestionnaireIdRef.current = null;
        transcriptionAutoLoadKeyRef.current = null;

        if (!visible) {
            return;
        }

        setCitationHighlightSegmentIndex(null);
        lastScrolledTranscriptionSegmentIndex.current = -1;
        transcriptionSegmentRefs.current = [];
    }, [selectedQuestionnaire?.id, visible]);

    useEffect(() => {
        if (!visible || !selectedQuestionnaire?.id || !jwtToken || isControlResultView) {
            return;
        }

        let isCancelled = false;
        setQuestionnaireDetailsLoading(true);
        setQuestionnaireDetailsError(null);

        fetchAudioControlById(selectedQuestionnaire.id, jwtToken)
            .then((response) => {
                if (isCancelled) {
                    return;
                }

                if (response.data?.statusCode === 200) {
                    const details = normalizeAudioControlDetails(response.data.data);
                    setQuestionnaireBlocks(details.blocks);
                    setQuestionnaireFailList(details.failList);
                    setSelectedFailList({});
                    setExpandedBlocks(buildInitialExpandedBlocks(details.blocks));
                } else {
                    setQuestionnaireBlocks([]);
                    setQuestionnaireFailList([]);
                    setSelectedFailList({});
                    setQuestionnaireDetailsError(response.data?.message || 'Не вдалося завантажити анкету');
                }
            })
            .catch(() => {
                if (!isCancelled) {
                    setQuestionnaireBlocks([]);
                    setQuestionnaireFailList([]);
                    setSelectedFailList({});
                    setQuestionnaireDetailsError('Не вдалося завантажити анкету');
                }
            })
            .finally(() => {
                if (!isCancelled) {
                    setQuestionnaireDetailsLoading(false);
                }
            });

        return () => {
            isCancelled = true;
        };
    }, [visible, selectedQuestionnaire?.id, jwtToken, isControlResultView]);

    const applyManualEvaluationState = useCallback((state: ManualEvaluationState) => {
        setElements(state.elements);
        setBlockScores(state.blockScores);
        setCitationPickTargetElementId(null);
        setPendingCitationSegmentIndexes([]);
        setExpandedCitations({});
    }, []);

    const getQualityControlId = useCallback(
        () => {
            if (isControlResultView) {
                return selectedQuestionnaire?.id ?? null;
            }

            return selectedQuestionnaire?.id ?? audioControlId ?? null;
        },
        [selectedQuestionnaire?.id, audioControlId, isControlResultView],
    );

    const persistManualControl = useCallback(async (blockIndex: number) => {
        const qualityControlId = getQualityControlId();
        if (!callId || !qualityControlId || !jwtToken) {
            throw new Error('Неможливо зберегти оцінку для цього дзвінка');
        }

        const blockPayloads = serializeManualControlBlocks(blocks, elements, {
            blockIndex,
        });

        if (blockPayloads.length === 0) {
            throw new Error('Оцініть хоча б один елемент');
        }

        const response = await saveManualControl(callId, qualityControlId, blockPayloads[0], jwtToken);
        if (response.data?.statusCode !== 200) {
            throw new Error(response.data?.message || 'Не вдалося зберегти оцінку');
        }

        return response;
    }, [
        getQualityControlId,
        callId,
        jwtToken,
        blocks,
        elements,
    ]);

    const completeManualControl = useCallback(async () => {
        const qualityControlId = getQualityControlId();
        if (!callId || !qualityControlId || !jwtToken) {
            throw new Error('Неможливо зберегти оцінку для цього дзвінка');
        }

        const response = await completeControl(callId, qualityControlId, jwtToken);
        if (response.data?.statusCode !== 200) {
            throw new Error(response.data?.message || 'Не вдалося завершити оцінку');
        }

        return response;
    }, [getQualityControlId, callId, jwtToken]);

    useEffect(() => {
        if (
            !visible
            || selectedQuestionnaire
            || !questionnaireOptions.length
        ) {
            return;
        }

        const matchedQuestionnaire = audioControlId != null
            ? questionnaireOptions.find((option) => option.id === Number(audioControlId))
            : undefined;

        if (matchedQuestionnaire) {
            setSelectedQuestionnaire(matchedQuestionnaire);
            return;
        }

        if (isControlResultView) {
            setSelectedQuestionnaire(questionnaireOptions[0]);
        }
    }, [visible, selectedQuestionnaire, questionnaireOptions, audioControlId, isControlResultView]);

    useEffect(() => {
        if (
            !visible
            || !isManualMode
            || isControlResultView
            || questionnaireDetailsLoading
            || questionnaireBlocks.length === 0
            || callId
        ) {
            return;
        }

        const manualSeed = getInitialEvaluationForMode(
            'manual',
            manualInitialEvaluation,
            aiInitialEvaluation,
            initialEvaluation,
        );

        if (manualSeed) {
            applyManualEvaluationState(
                resolveInitialManualEvaluationState(manualSeed, questionnaireBlocks),
            );
        }
    }, [
        visible,
        isManualMode,
        isControlResultView,
        questionnaireDetailsLoading,
        questionnaireBlocks,
        callId,
        manualInitialEvaluation,
        aiInitialEvaluation,
        initialEvaluation,
        applyManualEvaluationState,
    ]);

    const applyControlResultResponse = useCallback((data: unknown) => {
        const nextState = buildManualEvaluationState(data, questionnaireBlocks);
        applyManualEvaluationState(nextState);
    }, [questionnaireBlocks, applyManualEvaluationState]);

    const refreshEvaluatedQuestionnaire = useCallback(async (
        nextCallId: number,
        nextQualityControlId: number,
        nextJwtToken: string,
    ) => {
        const [resultResponse, formsResponse] = await Promise.all([
            fetchControlResult(nextCallId, nextQualityControlId, nextJwtToken),
            fetchFormsByCallDetailId(nextCallId, false, nextJwtToken),
        ]);

        if (!dialogActiveRef.current) {
            return;
        }

        if (resultResponse.data?.statusCode === 200) {
            const resultData = resultResponse.data.data;
            if (!isControlResultNotFound(resultData)) {
                setControlResultNotFound(false);
                setControlResultMeta(normalizeControlResultData(resultData).envelope);
                applyControlResultResponse(resultData);
            }
        }

        if (formsResponse.data?.statusCode === 200) {
            const nextOptions = normalizeSimpleUsersControls(formsResponse.data.data, {
                isRated: false,
            });

            setQuestionnaireOptions((previousOptions) => {
                const evaluatedOption = previousOptions.find((option) => option.id === nextQualityControlId);
                const nextWithoutEvaluated = nextOptions.filter((option) => option.id !== nextQualityControlId);

                if (!evaluatedOption) {
                    return nextWithoutEvaluated;
                }

                return [toEvaluatedQuestionnaireOption(evaluatedOption), ...nextWithoutEvaluated];
            });
        }
    }, [applyControlResultResponse]);

    useEffect(() => {
        if (!visible || !isControlResultView || !callId || !jwtToken || !selectedQuestionnaire?.id) {
            return;
        }

        const qualityControlId = selectedQuestionnaire.id;
        if (controlResultReadyForQuestionnaireIdRef.current === qualityControlId) {
            return;
        }

        let isCancelled = false;
        setControlResultLoading(true);

        fetchControlResult(callId, qualityControlId, jwtToken)
            .then((response) => {
                if (isCancelled) {
                    return;
                }

                if (response.data?.statusCode !== 200) {
                    throw new Error(response.data?.message || 'Не вдалося завантажити оцінку');
                }

                const resultData = response.data.data;
                if (isControlResultNotFound(resultData)) {
                    setControlResultNotFound(true);
                    setControlResultMeta(null);
                    setQuestionnaireBlocks([]);
                    setQuestionnaireFailList([]);
                    applyManualEvaluationState({elements: {}, blockScores: {}});
                    return;
                }

                const questionnaire = buildQuestionnaireFromControlResult(resultData);
                setControlResultNotFound(false);
                setQuestionnaireBlocks(questionnaire.blocks);
                setQuestionnaireFailList(questionnaire.failList);
                setControlResultMeta(questionnaire.envelope);
                setExpandedBlocks({});
                applyManualEvaluationState(questionnaire.state);
            })
            .catch((error) => {
                if (isCancelled) {
                    return;
                }

                dispatch({
                    type: SET_USER_STATUS_ERROR,
                    payload: {
                        message: error instanceof Error ? error.message : 'Не вдалося завантажити оцінку',
                    },
                });
            })
            .finally(() => {
                if (!isCancelled) {
                    setControlResultLoading(false);
                    controlResultReadyForQuestionnaireIdRef.current = qualityControlId;
                }
            });

        return () => {
            isCancelled = true;
        };
    }, [
        visible,
        isControlResultView,
        callId,
        jwtToken,
        selectedQuestionnaire?.id,
        applyManualEvaluationState,
        dispatch,
    ]);

    useEffect(() => {
        if (
            !visible
            || isControlResultView
            || !isManualMode
            || !callId
            || !jwtToken
            || questionnaireDetailsLoading
            || questionnaireBlocks.length === 0
        ) {
            return;
        }

        const qualityControlId = getQualityControlId();
        if (!qualityControlId) {
            return;
        }

        let isCancelled = false;
        setControlResultLoading(true);

        fetchControlResult(callId, qualityControlId, jwtToken)
            .then((response) => {
                if (isCancelled) {
                    return;
                }

                if (response.data?.statusCode !== 200) {
                    throw new Error(response.data?.message || 'Не вдалося завантажити оцінку');
                }

                const resultData = response.data.data;
                if (isControlResultNotFound(resultData)) {
                    setControlResultNotFound(false);
                    setControlResultMeta(null);
                    return;
                }

                setControlResultNotFound(false);
                setControlResultMeta(normalizeControlResultData(resultData).envelope);
                applyControlResultResponse(resultData);
            })
            .catch((error) => {
                if (isCancelled) {
                    return;
                }

                dispatch({
                    type: SET_USER_STATUS_ERROR,
                    payload: {
                        message: error instanceof Error ? error.message : 'Не вдалося завантажити оцінку',
                    },
                });
            })
            .finally(() => {
                if (!isCancelled) {
                    setControlResultLoading(false);
                    controlResultReadyForQuestionnaireIdRef.current = selectedQuestionnaire?.id ?? null;
                }
            });

        return () => {
            isCancelled = true;
        };
    }, [
        visible,
        isControlResultView,
        isManualMode,
        callId,
        jwtToken,
        getQualityControlId,
        questionnaireDetailsLoading,
        selectedQuestionnaire?.id,
        applyControlResultResponse,
        dispatch,
    ]);

    useEffect(() => {
        if (!visible) {
            audioLoadAttemptedRef.current = false;
            return;
        }

        if (
            !hasPlayerVisiblePermission
            || audioUrl?.length
            || audioUnavailable
            || audioUrlLoading
            || !onLoadAudio
            || audioLoadAttemptedRef.current
        ) {
            return;
        }

        audioLoadAttemptedRef.current = true;
        onLoadAudio();
    }, [
        visible,
        hasPlayerVisiblePermission,
        audioUrl,
        audioUnavailable,
        audioUrlLoading,
        onLoadAudio,
    ]);

    useEffect(() => {
        if (!visible || !validAudioUrl?.length) {
            return;
        }

        const hasAudioType1 = validAudioUrl.some((item: { audioType: number }) => item.audioType === 1);
        const hasAudioType2 = validAudioUrl.some((item: { audioType: number }) => item.audioType === 2);

        if (hasAudioType1) {
            dispatch(setIsSpeacking({name: 'Запис розмови', code: 1}));
        } else if (hasAudioType2) {
            dispatch(setIsSpeacking({name: 'Запис дзвінка', code: 2}));
        }
    }, [visible, validAudioUrl, dispatch]);

    const isCallRecordingUnavailable = callStatus === 101 || callStatus === 102;

    const canSeekTranscription = useMemo(() => {
        if (!hasPlayerVisiblePermission || !audioUrl?.length || isCallRecordingUnavailable) {
            return false;
        }

        return validAudioUrl.some((item: { tempUrl?: string }) => Boolean(item?.tempUrl));
    }, [hasPlayerVisiblePermission, audioUrl, validAudioUrl, isCallRecordingUnavailable]);

    const handleCloseTranscription = () => {
        setShowTranscription(false);
        setCitationHighlightSegmentIndex(null);
        setTranscriptionSearchQuery('');
        setActiveSearchMatchIndex(0);
        setPlaybackTimeMs(0);
        setTranscriptionPending(false);
        lastScrolledTranscriptionSegmentIndex.current = -1;
    };

    const loadTranscriptionResults = useCallback(async () => {
        const loadGeneration = transcriptionLoadGenerationRef.current;

        const applyIfCurrent = (applyState: () => void) => {
            if (loadGeneration === transcriptionLoadGenerationRef.current) {
                applyState();
            }
        };

        if (!callId || !sessionId || !jwtToken) {
            applyIfCurrent(() => {
                setTranscriptionError('Неможливо завантажити розшифровку для цього дзвінка');
                setTranscriptionPending(false);
            });
            return 'error' as const;
        }

        try {
            const response = await fetchTranscriptionByCallId(callId, sessionId, jwtToken);

            if (loadGeneration !== transcriptionLoadGenerationRef.current) {
                return 'cancelled' as const;
            }

            const loadResult = resolveTranscriptionByCallIdResult(response.data);

            if (loadResult.type === 'loaded') {
                applyIfCurrent(() => {
                    setLoadedTranscriptionSegments(loadResult.segments);
                    setLoadedTranscription(formatTranscriptionSegments(loadResult.segments));
                    setTranscriptionError(null);
                    setTranscriptionPending(false);
                });
                return 'loaded' as const;
            }

            if (loadResult.type === 'pending') {
                applyIfCurrent(() => {
                    setLoadedTranscriptionSegments([]);
                    setLoadedTranscription(null);
                    setTranscriptionError(null);
                    setTranscriptionPending(true);
                });
                return 'pending' as const;
            }

            applyIfCurrent(() => {
                setLoadedTranscriptionSegments([]);
                setLoadedTranscription('- - -');
                setTranscriptionError(loadResult.message || 'Не вдалося завантажити розшифровку');
                setTranscriptionPending(false);
            });
            return 'error' as const;
        } catch {
            applyIfCurrent(() => {
                setLoadedTranscriptionSegments([]);
                setLoadedTranscription('- - -');
                setTranscriptionError('Не вдалося завантажити розшифровку');
                setTranscriptionPending(false);
            });
            return 'error' as const;
        }
    }, [callId, jwtToken, sessionId]);

    const handleLoadTranscription = async () => {
        const loadGeneration = transcriptionLoadGenerationRef.current;

        if (!callId || !sessionId || !jwtToken) {
            setTranscriptionError('Неможливо завантажити розшифровку для цього дзвінка');
            setShowTranscription(true);
            setPlaybackTimeMs(0);
            setTranscriptionPending(false);
            lastScrolledTranscriptionSegmentIndex.current = -1;
            return;
        }

        setShowTranscription(true);
        setPlaybackTimeMs(0);
        setTranscriptionLoading(true);
        setTranscriptionError(null);
        setTranscriptionPending(false);
        lastScrolledTranscriptionSegmentIndex.current = -1;

        await loadTranscriptionResults();

        if (loadGeneration === transcriptionLoadGenerationRef.current) {
            setTranscriptionLoading(false);
        }
    };

    useEffect(() => {
        if (
            !visible
            || !selectedQuestionnaire?.id
            || !callId
            || !sessionId
            || !jwtToken
            || controlResultLoading
            || questionnaireDetailsLoading
        ) {
            return;
        }

        const questionnaireId = selectedQuestionnaire.id;
        const shouldLoadControlResult = isControlResultView || (isManualMode && Boolean(callId));

        if (
            shouldLoadControlResult
            && controlResultReadyForQuestionnaireIdRef.current !== questionnaireId
        ) {
            return;
        }

        if (transcriptionAutoLoadKeyRef.current === questionnaireId) {
            return;
        }

        transcriptionAutoLoadKeyRef.current = questionnaireId;

        if (!hasEvaluationCitations(elements)) {
            return;
        }

        if (loadedTranscription != null || loadedTranscriptionSegments.length > 0) {
            setShowTranscription(true);
            return;
        }

        void handleLoadTranscription();
    }, [
        visible,
        selectedQuestionnaire?.id,
        callId,
        sessionId,
        jwtToken,
        controlResultLoading,
        questionnaireDetailsLoading,
        elements,
        isControlResultView,
        isManualMode,
        loadedTranscription,
        loadedTranscriptionSegments,
    ]);

    useEffect(() => {
        if (!visible || !callId || !sessionId || !transcriptionHubStatus) {
            return;
        }

        if (transcriptionHubStatus.callDetailId !== callId) {
            return;
        }

        if (transcriptionHubStatus.sessionId !== sessionId) {
            return;
        }

        if (transcriptionHubStatus.status === TRANSCRIPTION_HUB_STATUS.PROCESSING) {
            setTranscriptionPending(true);
            setTranscriptionError(null);
            return;
        }

        if (transcriptionHubStatus.status === TRANSCRIPTION_HUB_STATUS.UNSUCCESS) {
            if (!transcriptionPending && !showTranscription) {
                return;
            }

            setTranscriptionLoading(false);
            setTranscriptionPending(false);
            setLoadedTranscriptionSegments([]);
            setLoadedTranscription('- - -');
            setTranscriptionError('Не вдалося розшифрувати дзвінок');
            return;
        }

        if (transcriptionHubStatus.status !== TRANSCRIPTION_HUB_STATUS.SUCCESS || !transcriptionPending) {
            return;
        }

        let isCancelled = false;

        const reloadTranscription = async () => {
            setTranscriptionLoading(true);
            setTranscriptionError(null);

            const result = await loadTranscriptionResults();

            if (isCancelled || result === 'cancelled') {
                return;
            }

            setTranscriptionLoading(false);

            if (result === 'pending') {
                setTranscriptionPending(true);
            }
        };

        reloadTranscription();

        return () => {
            isCancelled = true;
        };
    }, [
        callId,
        loadTranscriptionResults,
        sessionId,
        showTranscription,
        transcriptionHubStatus,
        transcriptionPending,
        visible,
    ]);

    const transcriptionText = formatTranscriptionContent(loadedTranscription);

    const transcriptionSegments = useMemo(() => {
        if (loadedTranscriptionSegments.length > 0) {
            return loadedTranscriptionSegments;
        }

        if (transcriptionText === '- - -') {
            return [];
        }

        return parseTranscriptionTextToSegments(transcriptionText);
    }, [loadedTranscriptionSegments, transcriptionText]);

    const attachedCitationSegmentIds = useMemo(
        () => getAttachedCitationIdsForElement(elements, citationPickTargetElementId),
        [elements, citationPickTargetElementId],
    );

    const activeTranscriptionSegmentIndex = useMemo(() => {
        if (!transcriptionSegments.length || playbackTimeMs <= 0) {
            return -1;
        }

        return findActiveTranscriptionSegmentIndex(transcriptionSegments, playbackTimeMs / 1000);
    }, [transcriptionSegments, playbackTimeMs]);

    const transcriptionMatchingSegmentIndices = useMemo(
        () => getTranscriptionMatchingSegmentIndices(transcriptionSegments, transcriptionSearchQuery),
        [transcriptionSegments, transcriptionSearchQuery],
    );

    const transcriptionSearchMatchCount = useMemo(() => {
        if (transcriptionSegments.length > 0) {
            return countTranscriptionSearchMatches(transcriptionSegments, transcriptionSearchQuery);
        }

        return countTranscriptionTextSearchMatches(transcriptionText, transcriptionSearchQuery);
    }, [transcriptionSegments, transcriptionSearchQuery, transcriptionText]);

    const currentSearchSegmentIndex = transcriptionMatchingSegmentIndices[activeSearchMatchIndex] ?? null;

    const hasTranscriptionSearchQuery = Boolean(transcriptionSearchQuery.trim());
    const canNavigateTranscriptionSearch = transcriptionMatchingSegmentIndices.length > 0;
    const hasTranscriptionSearchResults = transcriptionSegments.length > 0
        ? canNavigateTranscriptionSearch
        : transcriptionSearchMatchCount > 0;

    useEffect(() => {
        setActiveSearchMatchIndex(0);
    }, [transcriptionSearchQuery]);

    const handleTranscriptionSearchPrevious = () => {
        if (!canNavigateTranscriptionSearch) {
            return;
        }

        setActiveSearchMatchIndex((previousIndex) => (
            previousIndex <= 0
                ? transcriptionMatchingSegmentIndices.length - 1
                : previousIndex - 1
        ));
    };

    const handleTranscriptionSearchNext = () => {
        if (!canNavigateTranscriptionSearch) {
            return;
        }

        setActiveSearchMatchIndex((previousIndex) => (
            previousIndex >= transcriptionMatchingSegmentIndices.length - 1
                ? 0
                : previousIndex + 1
        ));
    };

    const handleClearTranscriptionSearch = () => {
        setTranscriptionSearchQuery('');
        setActiveSearchMatchIndex(0);
    };

    const registerTranscriptionSegmentRef = useCallback((index: number, element: HTMLDivElement | null) => {
        transcriptionSegmentRefs.current[index] = element;
    }, []);

    const scrollToTranscriptionSegment = useCallback((
        segmentIndex: number,
        scrollBlock: ScrollLogicalPosition = 'center',
    ) => {
        if (segmentIndex < 0) {
            return;
        }

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                transcriptionSegmentRefs.current[segmentIndex]?.scrollIntoView({
                    behavior: 'smooth',
                    block: scrollBlock,
                });
            });
        });
    }, []);

    const scrollToTranscriptionTime = useCallback((
        timeMs: number,
        scrollBlock: ScrollLogicalPosition = 'center',
    ) => {
        const segmentIndex = findActiveTranscriptionSegmentIndex(
            transcriptionSegments,
            timeMs / 1000,
        );
        lastScrolledTranscriptionSegmentIndex.current = segmentIndex;
        scrollToTranscriptionSegment(segmentIndex, scrollBlock);
    }, [scrollToTranscriptionSegment, transcriptionSegments]);

    useEffect(() => {
        if (!hasTranscriptionSearchQuery || currentSearchSegmentIndex == null) {
            return;
        }

        scrollToTranscriptionSegment(currentSearchSegmentIndex, 'nearest');
    }, [currentSearchSegmentIndex, hasTranscriptionSearchQuery, scrollToTranscriptionSegment]);

    const handleTranscriptionSeek = useCallback((timestampMs: number) => {
        setPlaybackTimeMs(timestampMs);
        scrollToTranscriptionTime(timestampMs, 'center');

        if (canSeekTranscription) {
            setTimeStamp(timestampMs);
            setSeekNonce((previousNonce) => previousNonce + 1);
        }
    }, [canSeekTranscription, scrollToTranscriptionTime]);

    const handleCitationTimestampClick = useCallback((citation: ManualEvaluationCitation) => {
        if (!showTranscription) {
            setShowTranscription(true);
        }

        const displayCitation = resolveEvaluationCitationDisplay(citation, transcriptionSegments);
        let segmentIndex = -1;

        if (citation.id != null) {
            segmentIndex = findTranscriptionSegmentIndexById(transcriptionSegments, citation.id);
        }

        if (segmentIndex < 0 && displayCitation.start != null) {
            segmentIndex = findTranscriptionSegmentIndexByStart(transcriptionSegments, displayCitation.start);
        }

        setCitationHighlightSegmentIndex(segmentIndex >= 0 ? segmentIndex : null);

        if (displayCitation.start != null && Number.isFinite(displayCitation.start)) {
            handleTranscriptionSeek(displayCitation.start * 1000);
        }
    }, [showTranscription, transcriptionSegments, handleTranscriptionSeek]);

    const handlePlaybackSeek = useCallback((timeMs: number) => {
        setPlaybackTimeMs(timeMs);
        lastScrolledTranscriptionSegmentIndex.current = -1;
        scrollToTranscriptionTime(timeMs, 'center');
    }, [scrollToTranscriptionTime]);

    const handlePlaybackTimeUpdate = useCallback((timeMs: number) => {
        setPlaybackTimeMs(timeMs);
    }, []);

    useEffect(() => {
        transcriptionSegmentRefs.current = [];
        lastScrolledTranscriptionSegmentIndex.current = -1;
    }, [transcriptionSegments]);

    useEffect(() => {
        if (activeTranscriptionSegmentIndex < 0) {
            return;
        }

        if (lastScrolledTranscriptionSegmentIndex.current === activeTranscriptionSegmentIndex) {
            return;
        }

        lastScrolledTranscriptionSegmentIndex.current = activeTranscriptionSegmentIndex;
        scrollToTranscriptionSegment(activeTranscriptionSegmentIndex, 'nearest');
    }, [activeTranscriptionSegmentIndex, scrollToTranscriptionSegment]);

    const handleQuestionnaireChange = (questionnaire: QualityControlSimpleOption | null) => {
        setSelectedQuestionnaire(questionnaire);
        setElements({});
        setExpandedCitations({});
        setCitationPickTargetElementId(null);
        setCitationHighlightSegmentIndex(null);
        setPendingCitationSegmentIndexes([]);
        setBlockScores({});
        setControlResultLoading(false);
        setControlResultNotFound(false);
        setControlResultMeta(null);
        prevHasQuestionnaireFail.current = false;
        setSelectedFailList({});
        setExpandedBlocks({});
        setQuestionnaireBlocks([]);
        setQuestionnaireFailList([]);

        if (!questionnaire) {
            setSelectedFailList({});
            setQuestionnaireDetailsLoading(false);
            setQuestionnaireDetailsError(null);
        }
    };

    const hasQuestionnaireFail = useMemo(() => {
        return hasQuestionnaireLevelFail(blocks, elements) || hasSelectedFailListItem;
    }, [blocks, elements, hasSelectedFailListItem]);

    const isFullEvaluationSubmitBlocked = hasSelectedFailListItem;

    useEffect(() => {
        if (isReadOnly) {
            return;
        }

        if (hasSelectedFailListItem) {
            setBlockScores(() => {
                const next: ManualEvaluationBlockScoreMap = {};

                blocks.forEach((block, blockIndex) => {
                    if (isAudioControlBlockCounted(block) && isAudioControlBlockActive(block)) {
                        next[blockIndex] = 0;
                    }
                });

                return next;
            });
        } else if (prevHasQuestionnaireFail.current) {
            setBlockScores({});
        }

        prevHasQuestionnaireFail.current = hasSelectedFailListItem;
    }, [hasSelectedFailListItem, blocks, isReadOnly]);

    const calculateBlockInnerScore = (block: AudioControlBlock) => {
        if (hasBlockLevelFail(block, elements) || hasBlockQuestionnaireLevelFail(block, elements)) {
            return 0;
        }

        return calculateBlockInnerScoreFromElements(block, elements);
    };

    const getBlockFailScoreState = (block: AudioControlBlock) =>
        hasSelectedFailListItem
        || hasBlockLevelFail(block, elements)
        || hasBlockQuestionnaireLevelFail(block, elements);

    const totalScore = useMemo(() => {
        if (hasQuestionnaireFail) {
            return 0;
        }

        return blocks.reduce((total, block, blockIndex) => {
            if (!isAudioControlBlockCounted(block) || !isAudioControlBlockActive(block)) {
                return total;
            }

            const storedBlockScore = blockScores[blockIndex];
            if (storedBlockScore != null) {
                return total + storedBlockScore;
            }

            const blockWeight = Number(block.weight) || 0;
            return total + (blockWeight * calculateBlockInnerScore(block)) / 100;
        }, 0);
    }, [blocks, elements, blockScores, hasQuestionnaireFail]);

    const displayTotalScore = useMemo(() => {
        if (hasQuestionnaireFail) {
            return 0;
        }

        if (controlResultMeta?.score != null) {
            return Number(controlResultMeta.score) || 0;
        }

        return totalScore;
    }, [hasQuestionnaireFail, controlResultMeta?.score, totalScore]);

    const formatBlockScore = (score: number) => {
        if (score >= 99.95) {
            return 100;
        }
        return Math.round(score * 10) / 10;
    };

    const setElementStatus = (
        elementId: number,
        condition: AudioControlCondition,
        blockIndex: number,
        status: number,
    ) => {
        setElements((previousElements) => ({
            ...previousElements,
            [elementId]: createManualControlElement(elementId, condition, {
                description: previousElements[elementId]?.description ?? '',
                tag: previousElements[elementId]?.tag ?? '',
                status,
                isManual: true,
            }),
        }));
        setBlockScores((previousBlockScores) => ({
            ...previousBlockScores,
            [blockIndex]: null,
        }));
    };

    const setElementDescription = (
        elementId: number,
        condition: AudioControlCondition,
        value: string,
    ) => {
        setElements((previousElements) => ({
            ...previousElements,
            [elementId]: createManualControlElement(elementId, condition, {
                description: limitTextLength(value, VIOLATION_DESCRIPTION_MAX_LENGTH),
                tag: previousElements[elementId]?.tag ?? '',
                status: previousElements[elementId]?.status ?? MANUAL_CONTROL_ELEMENT_STATUS.NO_VIOLATION,
                isManual: true,
            }),
        }));
    };

    const buildCitationFromSegment = (segment: TranscriptionSegment): ManualEvaluationCitation | null => {
        if (segment.id == null || segment.id === '') {
            return null;
        }

        return {
            id: Number(segment.id),
            comment: '',
        };
    };

    const startCitationPick = (elementId: number) => {
        setCitationPickTargetElementId((previousTargetElementId) => {
            if (previousTargetElementId === elementId) {
                setPendingCitationSegmentIndexes([]);
                return null;
            }

            setPendingCitationSegmentIndexes([]);
            return elementId;
        });
    };

    const toggleCitationSegmentSelection = useCallback((segmentIndex: number, checked: boolean) => {
        const segment = transcriptionSegments[segmentIndex];
        const segmentCitationId = segment?.id != null && segment.id !== '' ? Number(segment.id) : null;

        if (
            checked
            && segmentCitationId != null
            && Number.isFinite(segmentCitationId)
            && attachedCitationSegmentIds.has(segmentCitationId)
        ) {
            return;
        }

        setPendingCitationSegmentIndexes((previousIndexes) => {
            if (checked) {
                if (previousIndexes.includes(segmentIndex)) {
                    return previousIndexes;
                }
                return [...previousIndexes, segmentIndex].sort((leftIndex, rightIndex) => leftIndex - rightIndex);
            }

            return previousIndexes.filter((index) => index !== segmentIndex);
        });
    }, [attachedCitationSegmentIds, transcriptionSegments]);

    useEffect(() => {
        setPendingCitationSegmentIndexes((previousIndexes) => {
            const nextIndexes = previousIndexes.filter((segmentIndex) => {
                const segment = transcriptionSegments[segmentIndex];
                const segmentCitationId = segment?.id != null && segment.id !== '' ? Number(segment.id) : null;

                return segmentCitationId == null || !attachedCitationSegmentIds.has(segmentCitationId);
            });

            return nextIndexes.length === previousIndexes.length ? previousIndexes : nextIndexes;
        });
    }, [attachedCitationSegmentIds, transcriptionSegments]);

    const attachPendingCitation = () => {
        if (citationPickTargetElementId == null || pendingCitationSegmentIndexes.length === 0) {
            return;
        }

        const newCitations = pendingCitationSegmentIndexes
            .map((segmentIndex) => transcriptionSegments[segmentIndex])
            .filter((segment): segment is TranscriptionSegment => Boolean(segment))
            .map(buildCitationFromSegment)
            .filter((citation): citation is ManualEvaluationCitation => citation != null);

        if (newCitations.length === 0) {
            return;
        }

        setElements((previousElements) => {
            const currentElement = previousElements[citationPickTargetElementId];
            const currentCitations = parseCitationsFromTag(currentElement?.tag);
            const existingIds = new Set(
                currentCitations
                    .map((citation) => citation.id)
                    .filter((id): id is number => id != null),
            );
            const uniqueNewCitations = newCitations.filter(
                (citation) => citation.id != null && !existingIds.has(citation.id),
            );

            if (uniqueNewCitations.length === 0) {
                return previousElements;
            }

            const nextTag = serializeCitationsToTag([...currentCitations, ...uniqueNewCitations]);

            if (currentElement) {
                return {
                    ...previousElements,
                    [citationPickTargetElementId]: {
                        ...currentElement,
                        tag: nextTag,
                    },
                };
            }

            return {
                ...previousElements,
                [citationPickTargetElementId]: {
                    elementId: citationPickTargetElementId,
                    description: '',
                    tag: nextTag,
                    status: MANUAL_CONTROL_ELEMENT_STATUS.NO_VIOLATION,
                    score: 0,
                    isManual: true,
                },
            };
        });
        setPendingCitationSegmentIndexes([]);
        setCitationPickTargetElementId(null);
    };

    const removeCitation = (elementId: number, citationIndex: number) => {
        setElements((previousElements) => {
            const currentElement = previousElements[elementId];
            if (!currentElement) {
                return previousElements;
            }

            const currentCitations = parseCitationsFromTag(currentElement.tag);
            const nextCitations = currentCitations.filter((_, index) => index !== citationIndex);

            return {
                ...previousElements,
                [elementId]: {
                    ...currentElement,
                    tag: serializeCitationsToTag(nextCitations),
                },
            };
        });
    };

    const setCitationComment = (elementId: number, citationIndex: number, comment: string) => {
        setElements((previousElements) => {
            const currentElement = previousElements[elementId];
            if (!currentElement) {
                return previousElements;
            }

            const currentCitations = parseCitationsFromTag(currentElement.tag);
            if (!currentCitations[citationIndex]) {
                return previousElements;
            }

            const nextCitations = [...currentCitations];
            nextCitations[citationIndex] = {
                ...nextCitations[citationIndex],
                comment: limitTextLength(comment, CITATION_COMMENT_MAX_LENGTH),
            };

            return {
                ...previousElements,
                [elementId]: {
                    ...currentElement,
                    tag: serializeCitationsToTag(nextCitations),
                },
            };
        });
    };

    const toggleCitationExpanded = (citationKey: string) => {
        setExpandedCitations((previousExpandedCitations) => ({
            ...previousExpandedCitations,
            [citationKey]: !previousExpandedCitations[citationKey],
        }));
    };

    const renderCitationMeta = (citation: ManualEvaluationCitation) =>
        renderCitationMetaContent(
            resolveEvaluationCitationDisplay(citation, transcriptionSegments),
            () => handleCitationTimestampClick(citation),
        );

    const renderCitationControls = (elementId: number) => {
        const elementCitations = parseCitationsFromTag(elements[elementId]?.tag);

        if (!showTranscription && elementCitations.length === 0) {
            return null;
        }

        if (isReadOnly && elementCitations.length === 0) {
            return null;
        }

        const isPickTarget = citationPickTargetElementId === elementId;
        const canAttach = isPickTarget && pendingCitationSegmentIndexes.length > 0;
        const attachLabel = pendingCitationSegmentIndexes.length > 0
            ? `Прив'язати (${pendingCitationSegmentIndexes.length})`
            : 'Прив\'язати';
        const canManageCitations = showTranscription && !isReadOnly;

        return (
            <div className="manual-evaluation-citation-section">
                {canManageCitations && (
                    <>
                        <div className="manual-evaluation-citation-actions">
                            <Button
                                type="button"
                                label="Вибрати фрагмент"
                                icon="pi pi-align-left"
                                className={classNames('p-button-sm manual-evaluation-citation-action', {
                                    'manual-evaluation-citation-action--active': isPickTarget,
                                })}
                                onClick={() => startCitationPick(elementId)}
                            />
                            <Button
                                type="button"
                                label={attachLabel}
                                icon="pi pi-link"
                                className="p-button-sm manual-evaluation-citation-action"
                                disabled={!canAttach}
                                onClick={attachPendingCitation}
                            />
                        </div>
                        {isPickTarget && (
                            <div className="manual-evaluation-citation-hint">
                                Відмітьте репліки чекбоксами біля часу в розшифровці та натисніть «Прив'язати». Фрагменти, уже прив'язані до цього пункту, недоступні для повторного вибору.
                            </div>
                        )}
                        {isPickTarget && pendingCitationSegmentIndexes.length > 0 && (
                            <div className="">
                                {pendingCitationSegmentIndexes.map((segmentIndex) => {
                                    const segment = transcriptionSegments[segmentIndex];
                                    if (!segment) {
                                        return null;
                                    }

                                    return (
                                        <div
                                            className="manual-evaluation-citation-pending-item"
                                            key={`pending-citation-${segmentIndex}`}
                                        >
                                            <span className="manual-evaluation-transcription-timestamp">
                                                {formatTranscriptionTimestamp(segment.start ?? 0)}
                                            </span>
                                            {' '}
                                            <span className="manual-evaluation-transcription-speaker">
                                                {resolveTranscriptionSpeakerLabel(segment.speakerId)}:
                                            </span>
                                            {' '}
                                            <span>{String(segment.text ?? '').trim()}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
                {elementCitations.length > 0 && (
                    <div className="manual-evaluation-citation-quotes">
                        <div className="manual-evaluation-citation-quotes-title">
                            Цитати з розшифровки ({elementCitations.length})
                        </div>
                        {elementCitations.map((citation, citationIndex) => {
                            const citationKey = getCitationKey(elementId, citationIndex);
                            const isCitationExpanded = Boolean(expandedCitations[citationKey]);
                            const displayCitation = resolveEvaluationCitationDisplay(citation, transcriptionSegments);
                            const citationPreviewText = displayCitation.text || (citation.id != null
                                ? `Цитата #${citation.id}`
                                : '');

                            return (
                            <div
                                className={classNames('manual-evaluation-citation-quote', {
                                    'manual-evaluation-citation-quote--collapsed': !isCitationExpanded,
                                })}
                                key={citationKey}
                            >
                                <div className="manual-evaluation-citation-quote-header">
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        className="manual-evaluation-citation-quote-header-toggle"
                                        aria-expanded={isCitationExpanded}
                                        onClick={() => toggleCitationExpanded(citationKey)}
                                        onKeyDown={(event) => {
                                            if (event.target !== event.currentTarget) {
                                                return;
                                            }
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                toggleCitationExpanded(citationKey);
                                            }
                                        }}
                                    >
                                        <i className={classNames('pi manual-evaluation-citation-quote-toggle-icon', {
                                            'pi-chevron-down': isCitationExpanded,
                                            'pi-chevron-right': !isCitationExpanded,
                                        })}/>
                                        <span className="manual-evaluation-citation-quote-header-content">
                                            <span className="manual-evaluation-citation-quote-header-title">
                                                {!isCitationExpanded && (
                                                    <span className="manual-evaluation-citation-quote-preview-meta">
                                                        {renderCitationMetaContent(
                                                            displayCitation,
                                                            () => handleCitationTimestampClick(citation),
                                                        )}
                                                    </span>
                                                )}
                                            </span>
                                            {!isCitationExpanded && citationPreviewText && (
                                                <span className="manual-evaluation-citation-quote-preview-text">
                                                    {citationPreviewText}
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    <div className="manual-evaluation-citation-quote-header-actions">
                                        {canManageCitations && (
                                            <Button
                                                type="button"
                                                icon="pi pi-times"
                                                className="p-button-text p-button-rounded p-button-sm manual-evaluation-citation-remove"
                                                aria-label="Видалити цитату"
                                                onClick={() => removeCitation(elementId, citationIndex)}
                                            />
                                        )}
                                    </div>
                                </div>
                                {isCitationExpanded && (
                                    <div className="manual-evaluation-citation-quote-body">
                                        {renderCitationMeta(citation)}
                                        <div className="manual-evaluation-citation-quote-text">{citationPreviewText}</div>
                                        <InputTextarea
                                            className="w-full mt-2"
                                            rows={2}
                                            autoResize
                                            maxLength={CITATION_COMMENT_MAX_LENGTH}
                                            placeholder="Коментар до цитати"
                                            disabled={isReadOnly}
                                            value={citation.comment ?? ''}
                                            onKeyDown={(event) => event.stopPropagation()}
                                            onChange={(event) => setCitationComment(elementId, citationIndex, event.target.value)}
                                        />
                                    </div>
                                )}
                            </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    const getSelectedFailListItems = () =>
        questionnaireFailList.filter((item, index) =>
            selectedFailList[getFailListItemKey(item, index)]
        );

    const blockHasUnattachedPendingCitations = (block: AudioControlBlock) => {
        if (citationPickTargetElementId == null || pendingCitationSegmentIndexes.length === 0) {
            return false;
        }

        return (block.conditions ?? []).some((condition) => {
            if (!isAudioControlConditionCounted(condition) || !isAudioControlConditionActive(condition)) {
                return false;
            }

            const elementId = pickElementId(condition.id);
            return elementId === citationPickTargetElementId;
        });
    };

    const isBlockReadyForEvaluation = (block: AudioControlBlock) => {
        if (blockHasUnattachedPendingCitations(block)) {
            return false;
        }

        const conditions = block.conditions ?? [];

        return conditions.every((condition) => {
            if (!isAudioControlConditionCounted(condition) || !isAudioControlConditionActive(condition)) {
                return true;
            }

            const elementId = pickElementId(condition.id);
            return elementId != null && elements[elementId] != null;
        });
    };

    const handleEvaluateBlock = async (blockIndex: number) => {
        const block = blocks[blockIndex];
        if (!block) {
            return;
        }

        if (blockHasUnattachedPendingCitations(block)) {
            dispatch({
                type: SET_USER_STATUS_ERROR,
                payload: {
                    message: 'Спочатку натисніть «Прив\'язати» для обраних цитат або зніміть їх виділення',
                },
            });
            return;
        }

        if (!isBlockReadyForEvaluation(block)) {
            return;
        }

        if (!isManualMode) {
            return;
        }

        setBlockSavingIndex(blockIndex);
        try {
            const response = await persistManualControl(blockIndex);
            if (response.data?.data) {
                const {envelope} = normalizeControlResultData(response.data.data);
                if (envelope) {
                    setControlResultMeta(envelope);
                }

                const savedState = buildManualEvaluationState(response.data.data, blocks);
                setElements((previousElements) => ({
                    ...previousElements,
                    ...savedState.elements,
                }));
                setBlockScores((previousBlockScores) => ({
                    ...previousBlockScores,
                    ...savedState.blockScores,
                }));
            } else {
                setBlockScores((previousBlockScores) => ({
                    ...previousBlockScores,
                    [blockIndex]: null,
                }));
            }
            if (dialogActiveRef.current) {
                dispatch({
                    type: SET_USER_STATUS_SUCCESS,
                    payload: {message: 'Блок збережено'},
                });
            }
        } catch (error) {
            if (dialogActiveRef.current) {
                dispatch({
                    type: SET_USER_STATUS_ERROR,
                    payload: {
                        message: error instanceof Error ? error.message : 'Не вдалося зберегти блок',
                    },
                });
            }
        } finally {
            setBlockSavingIndex(null);
        }
    };

    const allBlocksEvaluated = blocks.length > 0 && blocks.every((block, blockIndex) => {
        if (!isAudioControlBlockCounted(block) || !isAudioControlBlockActive(block)) {
            return true;
        }
        return blockScores[blockIndex] != null;
    });

    const canSubmitFullEvaluation = allBlocksEvaluated && !isFullEvaluationSubmitBlocked;

    const handleEvaluate = async () => {
        if (isFullEvaluationSubmitBlocked) {
            return;
        }

        setSubmitting(true);
        try {
            const qualityControlId = getQualityControlId();

            if (isManualMode && callId && jwtToken && qualityControlId) {
                await completeManualControl();
            }

            onEvaluate?.({
                callId: callId ?? null,
                audioControlId: qualityControlId,
                score: formatBlockScore(displayTotalScore),
                elements,
                blockScores,
                selectedFailList: getSelectedFailListItems(),
            });

            if (isManualMode && qualityControlId != null) {
                setSelectedQuestionnaire((previousQuestionnaire) => (
                    previousQuestionnaire
                        ? toEvaluatedQuestionnaireOption(previousQuestionnaire)
                        : previousQuestionnaire
                ));

                if (dialogActiveRef.current && callId && jwtToken) {
                    try {
                        await refreshEvaluatedQuestionnaire(callId, qualityControlId, jwtToken);
                    } catch {
                        // Keep the local evaluated questionnaire visible if refresh fails.
                    }
                }
            }

            if (dialogActiveRef.current) {
                dispatch({
                    type: SET_USER_STATUS_SUCCESS,
                    payload: {message: 'Оцінку анкети надіслано'},
                });
            }
        } catch (error) {
            if (dialogActiveRef.current) {
                dispatch({
                    type: SET_USER_STATUS_ERROR,
                    payload: {
                        message: error instanceof Error ? error.message : 'Не вдалося зберегти оцінку',
                    },
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleEvaluateClick = () => {
        if (isFullEvaluationSubmitBlocked) {
            return;
        }

        const shouldConfirmCompleteEvaluation = isManualMode
            && Boolean(callId)
            && Boolean(jwtToken)
            && Boolean(getQualityControlId());

        if (shouldConfirmCompleteEvaluation) {
            setShowCompleteEvaluationConfirm(true);
            return;
        }

        void handleEvaluate();
    };

    const handleAcceptCompleteEvaluation = () => {
        setShowCompleteEvaluationConfirm(false);
        void handleEvaluate();
    };

    const handleRejectCompleteEvaluation = () => {
        setShowCompleteEvaluationConfirm(false);
    };

    const isBlockExpanded = (blockIndex: number) => expandedBlocks[blockIndex] === true;

    const toggleBlockExpanded = (blockIndex: number) => {
        setExpandedBlocks((previousExpandedBlocks) => ({
            ...previousExpandedBlocks,
            [blockIndex]: !isBlockExpanded(blockIndex),
        }));
    };

    const renderBlockScoreValue = (
        storedBlockScore: number | null | undefined,
        hasQuestionnaireFailScore: boolean,
    ) => {
        if (storedBlockScore == null) {
            return <span className="manual-evaluation-block-score-value">- - -</span>;
        }

        return (
            <strong className={classNames({
                'p-error': hasQuestionnaireFailScore || storedBlockScore === 0,
                'text-green-500': storedBlockScore > 0 && !hasQuestionnaireFailScore,
            })}>
                {`${formatBlockScore(storedBlockScore)}%`}
            </strong>
        );
    };

    const calculateElementScore = (elementId: number): number | null => {
        if (hasSelectedFailListItem) {
            return 0;
        }

        const element = elements[elementId];
        if (!element) {
            return null;
        }

        return isNoViolationStatus(element.status) ? 100 : 0;
    };

    const renderElementScoreValue = (elementId: number) => {
        const elementScore = calculateElementScore(elementId);

        if (elementScore == null) {
            return <span className="manual-evaluation-element-score-value">- - -</span>;
        }

        return (
            <strong className={classNames('manual-evaluation-element-score-value', {
                'p-error': elementScore === 0,
                'text-green-500': elementScore > 0,
            })}>
                {`${formatBlockScore(elementScore)}%`}
            </strong>
        );
    };

    const renderFailScopeRadios = (elementId: number, condition: AudioControlCondition) => {
        const failScope = condition.failScope ?? 'element';
        const inputName = `fail-scope-${elementId}`;

        return (
            <div className="manual-evaluation-fail-scope">
                <div className="manual-evaluation-fail-scope-header">
                    {AUDIO_CONTROL_FAIL_SCOPE_OPTIONS.map((scopeOption) => (
                        <span key={`${inputName}-label-${scopeOption.value}`}>{scopeOption.label}</span>
                    ))}
                </div>
                <div className="manual-evaluation-fail-scope-options">
                    {AUDIO_CONTROL_FAIL_SCOPE_OPTIONS.map((scopeOption) => (
                        <div className="manual-evaluation-fail-scope-option" key={`${inputName}-${scopeOption.value}`}>
                            <RadioButton
                                inputId={`${inputName}-${scopeOption.value}`}
                                name={inputName}
                                value={scopeOption.value}
                                checked={failScope === scopeOption.value}
                                disabled
                            />
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderViolationRadios = (
        elementId: number,
        blockIndex: number,
        condition: AudioControlCondition,
    ) => {
        const currentOption = getElementStatusOption(elements[elementId]?.status);
        const inputName = `violation-${elementId}`;

        return (
            <div className="manual-evaluation-violation-panel">
                <div className="manual-evaluation-violation-metrics">
                    <div className="manual-evaluation-element-weight">
                        <div className="manual-evaluation-element-top-label">Вага елементу</div>
                        <div className="manual-evaluation-element-weight-value">
                            {formatWeight(condition.weight)}
                        </div>
                    </div>
                    <div className="manual-evaluation-element-score">
                        <div className="manual-evaluation-element-top-label">Оцінка елементу</div>
                        <div>
                            {renderElementScoreValue(elementId)}
                        </div>
                    </div>
                    {renderFailScopeRadios(elementId, condition)}
                </div>
                <div className="manual-evaluation-violation-header">
                    {VIOLATION_OPTIONS.map((option) => (
                        <span key={`${inputName}-label-${option.value}`}>{option.label}</span>
                    ))}
                </div>
                <div className="manual-evaluation-violation-options">
                    {VIOLATION_OPTIONS.map((option) => (
                        <div className="manual-evaluation-violation-option" key={`${inputName}-${option.value}`}>
                            <RadioButton
                                inputId={`${inputName}-${option.value}`}
                                name={inputName}
                                value={option.value}
                                checked={currentOption === option.value}
                                disabled={isReadOnly}
                                onChange={(changeEvent) => setElementStatus(
                                    elementId,
                                    condition,
                                    blockIndex,
                                    getStatusFromOption(changeEvent.value as ManualEvaluationViolationOption),
                                )}
                            />
                        </div>
                    ))}
                </div>
                <InputTextarea
                    className="manual-evaluation-violation-description w-full mt-2"
                    rows={2}
                    autoResize
                    maxLength={VIOLATION_DESCRIPTION_MAX_LENGTH}
                    placeholder="Опис порушення"
                    disabled={isReadOnly}
                    value={elements[elementId]?.description || ''}
                    onChange={(event) => setElementDescription(elementId, condition, event.target.value)}
                />
            </div>
        );
    };

    const renderBlock = (block: AudioControlBlock, blockIndex: number) => {
        if (!isAudioControlBlockCounted(block) || !isAudioControlBlockActive(block)) {
            return null;
        }

        const conditions = block.conditions ?? [];
        const storedBlockScore = blockScores[blockIndex];
        const hasUnattachedPendingCitations = blockHasUnattachedPendingCitations(block);
        const isBlockReady = isBlockReadyForEvaluation(block);
        const expanded = isBlockExpanded(blockIndex);

        return (
            <div
                className={classNames('manual-evaluation-block', {
                    'manual-evaluation-block--readonly': isReadOnly,
                    'manual-evaluation-block--collapsed': !expanded,
                })}
                key={`manual-eval-block-${blockIndex}`}
            >
                <button
                    type="button"
                    className="manual-evaluation-block-header"
                    onClick={() => toggleBlockExpanded(blockIndex)}
                    aria-expanded={expanded}
                >
                    <div className="manual-evaluation-block-header-main">
                        <i className={classNames('manual-evaluation-block-toggle-icon pi', {
                            'pi-chevron-down': expanded,
                            'pi-chevron-right': !expanded,
                        })}/>
                        <div>
                            <div className="manual-evaluation-block-top-label">Назва блоку</div>
                            <div className="manual-evaluation-block-name">{block.name}</div>
                        </div>
                    </div>
                    <div className="manual-evaluation-block-header-meta">
                        <div className="manual-evaluation-block-weight">
                            <div className="manual-evaluation-block-top-label">Вага блоку</div>
                            <div className="manual-evaluation-block-weight-value">{formatWeight(block.weight)}</div>
                        </div>
                        <div className="manual-evaluation-block-header-score">
                            <div className="manual-evaluation-block-top-label">Оцінка блоку</div>
                            <div>
                                {renderBlockScoreValue(storedBlockScore, getBlockFailScoreState(block))}
                            </div>
                        </div>
                    </div>
                </button>

                {expanded && (
                    <>
                        {renderQuestionnaireMetaGrid([
                            {label: 'Сценарій', value: block.comment},
                        ], 'manual-evaluation-meta-grid--block')}
                        <div className="manual-evaluation-elements">
                            {conditions.map((condition, conditionIndex) => {
                                if (!isAudioControlConditionCounted(condition) || !isAudioControlConditionActive(condition)) {
                                    return null;
                                }

                                const elementId = pickElementId(condition.id);
                                if (elementId == null) {
                                    return null;
                                }

                                return (
                                    <div className="manual-evaluation-element-row" key={`element-${elementId}`}>
                                        <div className="manual-evaluation-element-info">
                                            <div className="manual-evaluation-element-top-label">Елемент</div>
                                            <div className="manual-evaluation-element-text">
                                                {condition.condition}
                                            </div>
                                            {renderQuestionnaireMetaGrid([
                                                {label: 'Сценарій', value: condition.scenario},
                                                {label: 'Fail', value: condition.failCondition},
                                            ])}
                                        </div>
                                        <div className="manual-evaluation-element-evaluation">
                                            {renderViolationRadios(elementId, blockIndex, condition)}
                                            {renderCitationControls(elementId)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="manual-evaluation-block-footer">
                            {hasUnattachedPendingCitations && (
                                <small className="manual-evaluation-block-pending-citations-hint p-error block mb-2">
                                    Спочатку натисніть «Прив'язати» для обраних цитат або зніміть їх виділення
                                </small>
                            )}
                            {!isReadOnly && (
                                <Button
                                    label="Оцінити блок"
                                    icon="pi pi-check"
                                    className={classNames('p-button-sm', {
                                        'p-button-success': storedBlockScore != null,
                                    })}
                                    loading={blockSavingIndex === blockIndex}
                                    disabled={storedBlockScore != null || !isBlockReady || blockSavingIndex === blockIndex}
                                    onClick={() => handleEvaluateBlock(blockIndex)}
                                />
                            )}
                        </div>
                    </>
                )}
            </div>
        );
    };

    const renderTranscriptionButton = () => {
        return (
            <Button
                icon="pi pi-align-left"
                label="Розшифровати аудіо"
                className="p-button-sm manual-evaluation-transcription-button"
                disabled={showTranscription || !callId}
                loading={transcriptionLoading || transcriptionPending}
                onClick={handleLoadTranscription}
            />
        );
    };

    const renderAudioSection = () => {
        if (!hasPlayerVisiblePermission) {
            return null;
        }

        if (!audioUrl?.length) {
            return (
                <div className="manual-evaluation-audio">
                    <div className="manual-evaluation-audio-row">
                        <Button
                            icon="pi pi-play"
                            label="Прослухати запис дзвінка"
                            className="p-button-sm"
                            disabled={isCallRecordingUnavailable}
                            loading={audioUrlLoading}
                            onClick={() => {
                                setTimeStamp(null);
                                onLoadAudio?.();
                            }}
                        />
                        {renderTranscriptionButton()}
                    </div>
                </div>
            );
        }

        return (
            <div className="manual-evaluation-audio">
                <div className="manual-evaluation-audio-row">
                    <div className="manual-evaluation-audio-player">
                        <AudioPlayer
                            key={audioPlayerSessionKey}
                            audioUrl={validAudioUrl}
                            handleResetPlayer={handleResetPlayer}
                            timeStamp={timeStamp}
                            seekNonce={seekNonce}
                            setTimeStamp={setTimeStamp}
                            autoPlay={false}
                            speedPlacement="below"
                            isActive={visible}
                            onPlaybackTimeUpdate={handlePlaybackTimeUpdate}
                            onPlaybackSeek={handlePlaybackSeek}
                        />
                    </div>
                    {renderTranscriptionButton()}
                </div>
            </div>
        );
    };

    const renderQuestionnaireStatusBadge = (
        statusMeta: { label: string; badgeClassName: string },
    ) => (
        <span
            className={classNames(
                'customer-badge',
                'manual-evaluation-status-badge',
                statusMeta.badgeClassName,
            )}
            style={{borderRadius: '6px'}}
        >
            {statusMeta.label}
        </span>
    );

    const dialogTitle = isControlResultView ? 'Переглянути оцінку' : 'Оцінити дзвінок';

    const displayedQuestionnaireStatus = controlResultMeta?.status ?? selectedQuestionnaire?.status ?? null;
    const displayedQuestionnaireStatusMeta = getQuestionnaireStatusMeta(
        displayedQuestionnaireStatus,
        {isRated: isViewingEvaluatedQuestionnaire},
    );
    const shouldShowQuestionnaireStatusBadge = selectedQuestionnaire != null
        && !questionnaireOptionsLoading;

    const shouldShowControlResultMeta = Boolean(
        controlResultMeta && (
            controlResultMeta.userFullName
            || controlResultMeta.dateTimeStart
            || controlResultMeta.score != null
        ),
    );

    const renderControlResultMeta = () => {
        if (!shouldShowControlResultMeta || !controlResultMeta) {
            return null;
        }

        const evaluatorName = controlResultMeta.userFullName?.trim() || '- - -';
        const evaluationDate = controlResultMeta.dateTimeStart
            ? formatControlResultDateTime(controlResultMeta.dateTimeStart)
            : '- - -';
        const apiScore = controlResultMeta.score != null
            ? formatBlockScore(Number(controlResultMeta.score))
            : null;

        return (
            <div className="manual-evaluation-control-result-meta">
                <div className="manual-evaluation-control-result-meta-row">
                    <div className="manual-evaluation-control-result-meta-item">
                        <span className="manual-evaluation-control-result-meta-label">Оцінено:</span>
                        <span className="manual-evaluation-control-result-meta-value">{evaluatorName}</span>
                    </div>
                    <div className="manual-evaluation-control-result-meta-item">
                        <span className="manual-evaluation-control-result-meta-label">Дата оцінки:</span>
                        <span className="manual-evaluation-control-result-meta-value">{evaluationDate}</span>
                    </div>
                    <div className="manual-evaluation-control-result-meta-item">
                        <span className="manual-evaluation-control-result-meta-label">Оцінка:</span>
                        {apiScore == null ? (
                            <span className="manual-evaluation-control-result-meta-value">- - -</span>
                        ) : (
                            <span className={classNames('manual-evaluation-control-result-meta-value', {
                                'p-error': hasQuestionnaireFail || apiScore === 0,
                                'text-green-500': !hasQuestionnaireFail && apiScore > 0,
                            })}>
                                {`${apiScore}%`}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderFooterScore = () => {
        if (shouldShowControlResultMeta) {
            return null;
        }

        return (
            <span className={classNames('manual-evaluation-total-score', {
                'p-error': hasQuestionnaireFail,
            })}>
                Загальна оцінка: {formatBlockScore(displayTotalScore)}%
                {hasQuestionnaireFail && <span className="ml-2">(Fail = 0% на анкету)</span>}
            </span>
        );
    };

    const questionnaireDropdownPlaceholder = questionnaireOptionsLoading
        ? 'Завантаження...'
        : isControlResultView
            ? 'Оберіть анкету, щоб переглянути оцінку'
            : 'Оберіть анкету';

    const footer = isReadOnly ? (
        <div className="manual-evaluation-footer">
            {renderFooterScore()}
            <div className="manual-evaluation-footer-actions">
                <Button
                    label="Закрити"
                    icon="pi pi-times"
                    className="p-button-outlined"
                    onClick={onHide}
                />
            </div>
        </div>
    ) : (
        <div className="manual-evaluation-footer">
            {renderFooterScore()}
            <div className="manual-evaluation-footer-actions">
                <Button
                    label="Оцінити"
                    icon="pi pi-check"
                    className={classNames({
                        'p-button-success': canSubmitFullEvaluation,
                    })}
                    loading={submitting}
                    disabled={!selectedQuestionnaire || !canSubmitFullEvaluation}
                    onClick={handleEvaluateClick}
                />
            </div>
        </div>
    );

    return (
        <>
            <ConfirmDialog
                visible={showCompleteEvaluationConfirm}
                onHide={() => setShowCompleteEvaluationConfirm(false)}
                message="Бажаєте надіслати оцінку всієї анкети?"
                icon="pi pi-exclamation-triangle"
                acceptLabel="Так"
                rejectLabel="Ні"
                accept={handleAcceptCompleteEvaluation}
                reject={handleRejectCompleteEvaluation}
            />
            <Dialog
            header={dialogTitle}
            visible={visible}
            onHide={onHide}
            footer={footer}
            className={classNames('manual-evaluation-dialog', {
                'manual-evaluation-dialog--readonly': isReadOnly,
                'manual-evaluation-dialog--control-result-view': isViewingEvaluatedQuestionnaire,
            })}
            style={{width: '95vw', maxWidth: '1400px'}}
            dismissableMask
            draggable={false}
            blockScroll
        >
            <div className="manual-evaluation-sticky-header">
                <div className={classNames('manual-evaluation-header-row', {
                    'manual-evaluation-header-row--audio-only': !SHOW_QUESTIONNAIRE_EVALUATION_SCHEME,
                })}>
                    <div className={classNames('manual-evaluation-header-panel manual-evaluation-header-panel--audio', {
                        'manual-evaluation-header-panel--full-width': !SHOW_QUESTIONNAIRE_EVALUATION_SCHEME,
                    })}>
                        <div className="manual-evaluation-toolbar">
                            <div className="manual-evaluation-toolbar-controls">
                                <div className="manual-evaluation-questionnaire-select">
                                    <label htmlFor="manual-evaluation-questionnaire" className="manual-evaluation-questionnaire-label">
                                        Анкета
                                    </label>
                                    <Dropdown
                                        id="manual-evaluation-questionnaire"
                                        className="manual-evaluation-questionnaire-dropdown"
                                        value={selectedQuestionnaire}
                                        options={questionnaireOptions}
                                        optionLabel="searchLabel"
                                        dataKey="id"
                                        filter
                                        filterPlaceholder="Введіть назву"
                                        resetFilterOnHide
                                        showClear={isControlResultView || (!readOnly && isManualMode)}
                                        disabled={questionnaireOptionsLoading || (!isControlResultView && readOnly)}
                                        onChange={(event) => handleQuestionnaireChange(event.value ?? null)}
                                        placeholder={questionnaireDropdownPlaceholder}
                                    />
                                    {shouldShowQuestionnaireStatusBadge && (
                                        <span className="manual-evaluation-questionnaire-status">
                                            {renderQuestionnaireStatusBadge(displayedQuestionnaireStatusMeta)}
                                        </span>
                                    )}
                                </div>
                                {canSwitchEvaluationMode && (
                                    <div className="manual-evaluation-mode-select">
                                        <label htmlFor="manual-evaluation-mode" className="manual-evaluation-questionnaire-label">
                                            Тип оцінки
                                        </label>
                                        <Dropdown
                                            id="manual-evaluation-mode"
                                            className="manual-evaluation-mode-dropdown"
                                            value={selectedEvaluationMode}
                                            options={EVALUATION_MODE_OPTIONS}
                                            optionLabel="label"
                                            optionValue="value"
                                            onChange={(event) => handleEvaluationModeChange(event.value)}
                                        />
                                    </div>
                                )}
                            </div>
                            {renderAudioSection()}
                        </div>
                    </div>

                    {SHOW_QUESTIONNAIRE_EVALUATION_SCHEME && (
                        <div className="manual-evaluation-header-panel manual-evaluation-header-panel--scheme">
                            <button
                                type="button"
                                className={classNames('manual-evaluation-scheme-header', {
                                    'manual-evaluation-scheme-header--collapsed': !isFailPanelExpanded,
                                })}
                                onClick={() => setIsFailPanelExpanded((previousValue) => !previousValue)}
                                aria-expanded={isFailPanelExpanded}
                            >
                                <i className={classNames('manual-evaluation-scheme-toggle-icon pi', {
                                    'pi-chevron-down': isFailPanelExpanded,
                                    'pi-chevron-right': !isFailPanelExpanded,
                                })}/>
                                <span>Схема оцінювання анкети</span>
                            </button>

                            {isFailPanelExpanded && (
                                <div className="manual-evaluation-top-panels">
                                    <div className="manual-evaluation-fail-panel">
                                        <div className="manual-evaluation-fail-title">Fail = 0% на анкету</div>
                                        {questionnaireFailList.map((failListItem, index) => (
                                            <div className="manual-evaluation-fail-item" key={`questionnaire-fail-${index}`}>
                                                {failListItem.failItem}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                {renderControlResultMeta()}
            </div>

            <div className={classNames('manual-evaluation-content', {
                'manual-evaluation-content--no-transcription': !showTranscription,
            })}>
                {showTranscription && (
                    <aside className="manual-evaluation-transcription">
                        <div className="manual-evaluation-transcription-title">
                            <span>Розшифровка</span>
                            <Button
                                type="button"
                                icon="pi pi-times"
                                className="p-button-text p-button-rounded p-button-sm manual-evaluation-transcription-close"
                                aria-label="Закрити розшифровку"
                                onClick={handleCloseTranscription}
                            />
                        </div>
                        <div className="manual-evaluation-transcription-search">
                            <span className="p-input-icon-left manual-evaluation-transcription-search-input">
                                <i className="pi pi-search" />
                                <InputText
                                    value={transcriptionSearchQuery}
                                    onChange={(event) => setTranscriptionSearchQuery(event.target.value)}
                                    placeholder="Пошук у розшифровці"
                                    className="w-full"
                                />
                            </span>
                            {hasTranscriptionSearchQuery && (
                                <>
                                    <span className="manual-evaluation-transcription-search-meta">
                                        {canNavigateTranscriptionSearch
                                            ? `${activeSearchMatchIndex + 1}/${transcriptionMatchingSegmentIndices.length}`
                                            : transcriptionSearchMatchCount > 0
                                                ? `${transcriptionSearchMatchCount} збігів`
                                                : '0 збігів'}
                                    </span>
                                    {canNavigateTranscriptionSearch && (
                                        <>
                                            <Button
                                                type="button"
                                                icon="pi pi-chevron-up"
                                                className="p-button-text p-button-sm manual-evaluation-transcription-search-nav"
                                                aria-label="Попередній збіг"
                                                onClick={handleTranscriptionSearchPrevious}
                                            />
                                            <Button
                                                type="button"
                                                icon="pi pi-chevron-down"
                                                className="p-button-text p-button-sm manual-evaluation-transcription-search-nav"
                                                aria-label="Наступний збіг"
                                                onClick={handleTranscriptionSearchNext}
                                            />
                                        </>
                                    )}
                                    <Button
                                        type="button"
                                        icon="pi pi-times"
                                        className="p-button-text p-button-sm manual-evaluation-transcription-search-clear"
                                        aria-label="Очистити пошук"
                                        onClick={handleClearTranscriptionSearch}
                                    />
                                </>
                            )}
                        </div>
                        {citationPickTargetElementId != null && (
                            <div className="manual-evaluation-transcription-pick-banner">
                                Режим цитування: відмітьте репліки чекбоксами біля часу
                            </div>
                        )}
                        <div
                            className={classNames('manual-evaluation-transcription-body', {
                                'manual-evaluation-transcription-body--pick-mode': citationPickTargetElementId != null,
                            })}
                            ref={transcriptionBodyRef}
                        >
                            {transcriptionLoading || transcriptionPending ? (
                                <div className="manual-evaluation-transcription-text">
                                    {transcriptionPending && !transcriptionLoading
                                        ? 'Розшифровка в обробці...'
                                        : 'Завантаження розшифровки...'}
                                </div>
                            ) : transcriptionError ? (
                                <div className="manual-evaluation-transcription-text p-error">{transcriptionError}</div>
                            ) : (
                                <div className="manual-evaluation-transcription-text">
                                    {transcriptionText === '- - -' ? (
                                        transcriptionText
                                    ) : hasTranscriptionSearchQuery && !hasTranscriptionSearchResults ? (
                                        <span className="manual-evaluation-transcription-search-empty">
                                            Збігів не знайдено
                                        </span>
                                    ) : transcriptionSegments.length > 0 ? (
                                        renderTranscriptionSegments(
                                            transcriptionSegments,
                                            activeTranscriptionSegmentIndex,
                                            canSeekTranscription,
                                            handleTranscriptionSeek,
                                            registerTranscriptionSegmentRef,
                                            transcriptionSearchQuery,
                                            currentSearchSegmentIndex,
                                            citationHighlightSegmentIndex,
                                            citationPickTargetElementId != null,
                                            pendingCitationSegmentIndexes,
                                            attachedCitationSegmentIds,
                                            toggleCitationSegmentSelection,
                                        )
                                    ) : (
                                        renderTranscriptionWithTimestamps(
                                            transcriptionText,
                                            canSeekTranscription,
                                            handleTranscriptionSeek,
                                            transcriptionSearchQuery,
                                        )
                                    )}
                                </div>
                            )}
                        </div>
                    </aside>
                )}
                <div className={classNames('manual-evaluation-blocks', {
                    'manual-evaluation-blocks--full-width': !showTranscription,
                })}>
                    {!selectedQuestionnaire ? (
                        <div className="manual-evaluation-empty-state">
                            {isControlResultView
                                ? 'Оберіть анкету, щоб переглянути оцінку'
                                : 'Оберіть анкету, щоб переглянути блоки оцінювання'}
                        </div>
                    ) : questionnaireDetailsLoading || controlResultLoading ? (
                        <div className="manual-evaluation-empty-state">
                            {controlResultLoading ? 'Завантаження оцінки...' : 'Завантаження анкети...'}
                        </div>
                    ) : questionnaireDetailsError ? (
                        <div className="manual-evaluation-empty-state p-error">
                            {questionnaireDetailsError}
                        </div>
                    ) : isControlResultView && controlResultNotFound ? (
                        <div className="manual-evaluation-empty-state">
                            Оцінку за даною анкетою не знайдено
                        </div>
                    ) : blocks.length === 0 ? (
                        <div className="manual-evaluation-empty-state">
                            У цій анкеті немає блоків оцінювання
                        </div>
                    ) : (
                        <>
                            {blocks.map((block, blockIndex) => renderBlock(block, blockIndex))}
                        </>
                    )}
                </div>
            </div>
        </Dialog>
        </>
    );
};

// @ts-ignore
export default ManualEvaluationDialog;
